import { useEffect, useState } from "react";
import { StakeholderAddon, UploaderStakeholderEntity } from "./StakeholderAddon";
import { useCurrentOrganisationId } from "../../../../../../services/local/organisationContext/OrganisationContextModel";
import { GridTypes } from "../../../../../../enums";
import { STAKEHOLDER_GRID_UPLOADER_CONFIG } from "../../../../../../core/grids/builder/directorBuilder/uploader/config/StakeholderGridUploader_config";
import toastService from "../../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../../core/toaster/Toaster_model";
import I18n from "../../../../../../core/localization/I18n";
import { Button, ButtonTypes } from "../../../../Button";

export const StakeholderAddonView: React.FC<{
  uploadedData: any[];
  processedEntities: Record<string, any[]>;
  onComplete: (result: Partial<UploaderStakeholderEntity>[]) => void;
}> = ({ uploadedData, processedEntities, onComplete }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [existingStakeholders, setExistingStakeholders] = useState<Partial<UploaderStakeholderEntity>[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [extractedStakeholders, setExtractedStakeholders] = useState<Partial<UploaderStakeholderEntity>[]>([]);
  const [mappedStakeholders, setMappedStakeholders] = useState<Partial<UploaderStakeholderEntity>[]>([]);
  const [activeTab, setActiveTab] = useState<"mapping" | "new" | "matched">("mapping");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [editStakeholder, setEditStakeholder] = useState<Partial<UploaderStakeholderEntity> | null>(null);
  const organisationId = useCurrentOrganisationId();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const addon = new StakeholderAddon(
          () => {},
          () => {},
          {
            organisationId: organisationId,
            gridType: GridTypes.STAKEHOLDERS_GRID,
            fields: STAKEHOLDER_GRID_UPLOADER_CONFIG,
            registeredAddons: []
          }
        );

        const existingStakeholdersData = await addon.getExistingEntities();
        setExistingStakeholders(existingStakeholdersData);

        const extractedStakeholdersData = addon.extractStakeholdersFromData(uploadedData);
        setExtractedStakeholders(extractedStakeholdersData);

        const mappedStakeholdersData = await addon.mapEntities(uploadedData, existingStakeholdersData);
        setMappedStakeholders(mappedStakeholdersData);
      } catch (error) {
        console.error("Error loading stakeholder data:", error);
        toastService
          .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
          .setContent(<span>Failed to load stakeholder data</span>);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [uploadedData, organisationId]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleMatchStakeholder = (stakeholder: UploaderStakeholderEntity) => {};

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleCreateNew = (stakeholder: UploaderStakeholderEntity) => {
    const newStakeholder = {
      ...stakeholder,
      isNew: true,
      isMatched: false
    };
    setEditStakeholder(newStakeholder);
  };

  const handleSaveEdit = () => {
    if (!editStakeholder) return;

    const updatedStakeholders = mappedStakeholders.map(stakeholder =>
      stakeholder === editStakeholder || stakeholder.email === editStakeholder.email ? editStakeholder : stakeholder
    );

    setMappedStakeholders(updatedStakeholders);
    setEditStakeholder(null);
  };

  const handleComplete = () => {
    onComplete(mappedStakeholders);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getFilteredData = () => {
    switch (activeTab) {
      case "new":
        return mappedStakeholders.filter(stakeholder => stakeholder.isNew);
      case "matched":
        return mappedStakeholders.filter(stakeholder => stakeholder.isMatched);
      case "mapping":
      default:
        return mappedStakeholders;
    }
  };

  const newCount = mappedStakeholders.filter(s => s.isNew).length;
  const matchedCount = mappedStakeholders.filter(s => s.isMatched).length;

  return (
    <div className="col">
      <div className="row d-flex flex-column">
        <h3>{I18n.t("phrases.stakeholderValidation") || "Stakeholder Validation"}</h3>
        <p>
          {I18n.t("phrases.stakeholderMapping") ||
            "Review and validate stakeholder mappings with their business areas and roles."}
        </p>

        {processedEntities && (
          <div className="dependency-info">
            <p className="text-muted">
              Dependencies processed:
              {processedEntities.business_area && ` ${processedEntities.business_area.length} Business Areas`}
              {processedEntities.role && `, ${processedEntities.role.length} Roles`}
            </p>
          </div>
        )}
      </div>

      <div className="row">
        <div className={`tab ${activeTab === "mapping" ? "active" : ""}`} onClick={() => setActiveTab("mapping")}>
          All ({mappedStakeholders.length})
        </div>
        <div className={`tab ${activeTab === "new" ? "active" : ""}`} onClick={() => setActiveTab("new")}>
          New ({newCount})
        </div>
        <div className={`tab ${activeTab === "matched" ? "active" : ""}`} onClick={() => setActiveTab("matched")}>
          Matched ({matchedCount})
        </div>
      </div>

      <div className="row" style={{ height: "500px" }}>
        {/* grid */}
      </div>

      <div className="row d-flex justify-content-end mt-2">
        <Button className="mx-2" type={ButtonTypes.PRIMARY} isDisabled={isLoading} onClick={handleComplete}>
          <span>{I18n.t("phrases.next") || "Next"}</span>
        </Button>
      </div>

      {editStakeholder && (
        <div className="edit-modal">
          <div className="modal-content">
            <h4>Edit Stakeholder</h4>
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                value={editStakeholder.firstName || ""}
                onChange={e => setEditStakeholder({ ...editStakeholder, firstName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                value={editStakeholder.lastName || ""}
                onChange={e => setEditStakeholder({ ...editStakeholder, lastName: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editStakeholder.email || ""}
                onChange={e => setEditStakeholder({ ...editStakeholder, email: e.target.value })}
              />
            </div>
            <div className="form-actions">
              <Button type={ButtonTypes.SECONDARY} onClick={() => setEditStakeholder(null)}>
                Cancel
              </Button>
              <Button type={ButtonTypes.PRIMARY} onClick={handleSaveEdit}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
